/**
 * Rejestr etykiet kolumn per moduł systemowy — dla Kreatora Listy (zakładka
 * Kolumny). Generyczny widok kreatora zna tylko `slug` z URL, a definicje kolumn
 * (z `renderCell` + `t()`) żyją w komponentach stron (`Companies.tsx`, `Employees.tsx`).
 *
 * Listy ZMIGROWANE na silnik data-driven (7 klas dokumentów + kontrahenci) biorą
 * etykiety z KATALOGU pól (`availableFields` z providera buildera / menu-embed;
 * id = slug) — ścieżka catalog-first w `useColumnLabel` niżej. Ten rejestr zostaje
 * fallbackiem dla: (a) list niezmigrowanych (stare krótkie id), (b) kolumn spoza
 * katalogu — `company` i kolumny procesu w menu-embed, gdzie `availableFields`
 * niesie tylko pola kartoteki. Id nieznane nigdzie → panel pokazuje surowe id.
 *
 * Hub Krok 2: `useColumnLabel(slug)` na dole pliku — dynamiczny provider etykiet
 * dla trybu `template` (etykiety z definicji pól szablonu zamiast rejestru).
 */

import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { KARTOTEKA_EXTRA_LIST_COLUMNS, kartotekaListFieldLabelKey } from '@saas/shared';

import { useReferenceListContext } from '@/contexts/ReferenceListContext';
import {
  parseTemplateTableId,
  useModuleTemplateDetail,
} from '@/lib/module-templates/template-layouts';

// Kolumny list spoza katalogu pól (Firma, Kategoria, szczegóły umowy, Nadawca, kolumny procesu,
// syntetyczne kolumny pracowników/użytkowników/kontaktów) — jedno źródło w `@saas/shared`
// (`KARTOTEKA_EXTRA_LIST_COLUMNS`), wspólne z kreatorem listy w panelu ITE.
const COLUMN_LABEL_KEYS: Record<string, Record<string, string>> = Object.fromEntries(
  Object.entries(KARTOTEKA_EXTRA_LIST_COLUMNS).map(([slug, cols]) => [
    slug,
    Object.fromEntries(cols.map((c) => [c.id, c.labelI18nKey])),
  ]),
);

/** Klucz i18n etykiety kolumny dla danego modułu, albo undefined gdy nieznana. */
export function getColumnLabelKey(slug: string, columnId: string): string | undefined {
  return COLUMN_LABEL_KEYS[slug]?.[columnId];
}

/**
 * Id kolumn zarejestrowanych dla modułu, których NIE ma w katalogu pól (np. `company` —
 * kompozytowa kolumna „Firma" renderowana nadpisaniem strony, albo `active_process_name`/
 * `current_stage` poza scope='system'). Używane przez `ListColumnsPanel` jako część zbioru
 * DOZWOLONEGO przy soft-healu zapisu — te id nie są sierotami, mimo że `availableFields`
 * (katalog pól) ich nie niesie. Bez tego soft-heal wycinałby realne, renderowane kolumny.
 */
export function getRegisteredColumnIds(slug: string): string[] {
  return Object.keys(COLUMN_LABEL_KEYS[slug] ?? {});
}

/**
 * Resolver etykiet kolumn świadomy trybu `template` (hub Krok 2). Statyczny rejestr
 * wyżej zna tylko sługi systemowe — dla `tableId` widoku listy SZABLONU etykiety
 * pochodzą z definicji pól encji w ADM (nazwa pola po slugu) plus wbudowane
 * `record_name`/`record_number` z i18n. Poza trybem template: dotychczasowa ścieżka
 * (rejestr → i18n). Zwraca funkcję `(columnId) → etykieta | undefined` (undefined =
 * pokaż surowe id — zachowanie paneli bez zmian).
 */
export function useColumnLabel(slug: string): (columnId: string) => string | undefined {
  const { t } = useTranslation();
  const templateRef = parseTemplateTableId(slug);
  const templateQuery = useModuleTemplateDetail(templateRef?.code, {
    enabled: templateRef !== null,
  });
  const templateEntity = templateRef
    ? templateQuery.data?.entities.find((e) => e.slug === templateRef.entitySlug)
    : undefined;

  // Encja-odwołanie: template entity.fields jest puste (pola żyją w kartotece).
  // Kontekst ReferenceListContext dostarcza pola kartoteki bez zmiany sygnatury paneli.
  const referenceCtx = useReferenceListContext();

  return useCallback(
    (columnId: string) => {
      if (templateRef) {
        if (columnId === 'record_name') {
          return t('moduleTemplateEditor.layoutDesigner.builtinColumns.recordName');
        }
        if (columnId === 'record_number') {
          return t('moduleTemplateEditor.layoutDesigner.builtinColumns.recordNumber');
        }
        // Najpierw: pola encji szablonu (encja własna z polami w szablonie)
        const fromTemplate = templateEntity?.fields.find((f) => f.slug === columnId)?.name;
        if (fromTemplate) return fromTemplate;
        // Fallback: pola kartoteki (encja-odwołanie — template entity.fields puste)
        if (referenceCtx) {
          return referenceCtx.kartotekaFields.find((f) => f.slug === columnId)?.name;
        }
        return undefined;
      }
      // Kartoteki systemowe na silniku data-driven: etykieta z KATALOGU pól
      // (availableFields z providera buildera / menu-embed; id = slug) — kreator
      // pokazuje te same nazwy co nagłówki listy. Rejestr statyczny = fallback
      // dla list niezmigrowanych i kolumn spoza katalogu (company, kolumny
      // procesu w menu-embed).
      // Menu-embed (wymuszony layout) podaje tableId `<slug>::layout-<id>` — rejestr
      // jest kluczowany czystym slugiem modułu, więc sufiks obcinamy.
      const registrySlug = slug.split('::')[0] ?? slug;
      const fromCatalog = referenceCtx?.availableFields?.find((f) => f.id === columnId)?.name;
      if (fromCatalog) {
        // Pole, które lista klienta podpisuje inaczej niż katalog (pracownicy: `last_name` →
        // „Imię i nazwisko") — ta sama nazwa co w palecie kreatora (`resolveListColumnSet`).
        const overrideKey = kartotekaListFieldLabelKey(registrySlug, columnId);
        return overrideKey ? t(overrideKey) : fromCatalog;
      }
      const key = getColumnLabelKey(registrySlug, columnId);
      return key ? t(key) : undefined;
    },
    [slug, t, templateRef, templateEntity, referenceCtx],
  );
}
